"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from "react";
import { cx } from "../internal/cx.js";
import { IconSearch, IconSparkles } from "../internal/icons.js";
import { Badge } from "./Badge.js";
import { Button } from "./Button.js";
export const PLACE_GUIDE_TEXTS = {
    what: "Что",
    how: "Как",
    ok: "Понятно",
    miss: (label) => `Место не нашлось на экране — смотрите: ${label}`,
};
const WIDTH = 340;
const GAP = 12;
/**
 * A place on its screen: a pulsing ring over the element and a callout next to it — what changed, what to look at, how
 * to use it; no element — the callout at the top says where to look. Fixed over the page, follows the element while it
 * moves. Esc closes it unless something above already took the key (a menu, a popover — the topmost layer wins).
 */
export function PlaceGuide({ guide, target, onClose, scroll = true, texts, className }) {
    const t = { ...PLACE_GUIDE_TEXTS, ...texts };
    const ring = useRef(null);
    const card = useRef(null);
    const ok = useRef(null);
    const close = useRef(onClose);
    close.current = onClose;
    useEffect(() => {
        if (target && scroll) {
            try {
                target.scrollIntoView({ block: "center", behavior: "smooth" });
            }
            catch {
                target.scrollIntoView();
            }
        }
        ok.current?.focus({ preventScroll: true });
    }, [target, scroll]);
    useEffect(() => {
        if (!target)
            return;
        let frame = 0;
        const place = () => {
            const r = target.getBoundingClientRect();
            if (ring.current)
                Object.assign(ring.current.style, { top: `${r.top - 4}px`, left: `${r.left - 4}px`, width: `${r.width + 8}px`, height: `${r.height + 8}px` });
            if (card.current) {
                const width = Math.min(WIDTH, window.innerWidth - 2 * GAP);
                const below = r.bottom + GAP;
                const top = below + 160 < window.innerHeight ? below : Math.max(GAP, r.top - GAP - card.current.offsetHeight);
                const left = Math.min(Math.max(GAP, r.left), window.innerWidth - width - GAP);
                Object.assign(card.current.style, { top: `${top}px`, left: `${left}px`, width: `${width}px` });
            }
            frame = window.requestAnimationFrame(place);
        };
        place();
        return () => window.cancelAnimationFrame(frame);
    }, [target]);
    useEffect(() => {
        // Bubble phase: a layer that handled Esc first (prevented or stopped it) keeps the guide open.
        const onKey = (event) => {
            if (event.key !== "Escape" || event.defaultPrevented)
                return;
            close.current();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);
    const hint = guide.hint?.trim();
    const how = guide.how?.trim();
    return _jsxs("div", { className: cx("jui-guide", className), children: [target && _jsx("div", { ref: ring, className: "jui-guide-ring", "aria-hidden": "true" }), _jsxs("div", { ref: card, className: cx("jui-guide-card", !target && "jui-guide-card--top"), role: "dialog", "aria-label": guide.title, children: [_jsxs("div", { className: "jui-guide-head", children: [_jsx("span", { className: "jui-guide-icon", children: target ? _jsx(IconSparkles, { small: true }) : _jsx(IconSearch, { small: true }) }), _jsx("strong", { className: "jui-guide-title", children: guide.title })] }), _jsx("div", { className: target ? "jui-guide-label" : "jui-guide-miss", children: target ? guide.label : t.miss(guide.label) }), hint && _jsxs("div", { className: "jui-guide-line", children: [_jsx(Badge, { className: "jui-guide-tag", children: t.what }), _jsx("span", { children: hint })] }), how && _jsxs("div", { className: "jui-guide-line", children: [_jsx(Badge, { className: "jui-guide-tag", children: t.how }), _jsx("span", { children: how })] }), _jsx(Button, { ref: ok, className: "jui-guide-ok", variant: "filled", size: "sm", onClick: () => close.current(), children: t.ok })] })] });
}
